import { httpClient } from './http-client';
import type { ApiResponse } from '../types/api';

export interface CashFlowPeriod {
  period: string;
  income: string;
  expenses: string;
  net: string;
}

export interface SpendingByCategory {
  category_id?: string | null;
  category_name: string;
  group_name: string;
  group_type: string;
  color?: string | null;
  amount: string;
  percentage: string;
  count: number;
}

export interface IncomeVsExpense {
  total_income: string;
  total_expenses: string;
  net_savings: string;
  savings_rate: string;
}

interface CategoryTrend {
  period: string;
  category_id: string;
  category_name: string;
  color?: string | null;
  amount: string;
}

export class ReportsService {
  private static readonly BASE_URL = '/reports';

  static async getCashFlow(params?: {
    start_date?: string;
    end_date?: string;
    granularity?: 'monthly' | 'yearly';
  }): Promise<ApiResponse<{ periods: CashFlowPeriod[]; granularity: string }>> {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);
    if (params?.granularity) searchParams.set('granularity', params.granularity);
    const url = searchParams.toString()
      ? `${ReportsService.BASE_URL}/cash-flow?${searchParams.toString()}`
      : `${ReportsService.BASE_URL}/cash-flow`;
    const raw = await httpClient.get<any>(url);
    const data = (raw as any).data || raw;
    return { data };
  }

  static async getSpendingByCategory(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<ApiResponse<{ categories: SpendingByCategory[]; total_amount: string }>> {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);
    const url = searchParams.toString()
      ? `${ReportsService.BASE_URL}/spending-by-category?${searchParams.toString()}`
      : `${ReportsService.BASE_URL}/spending-by-category`;
    const raw = await httpClient.get<any>(url);
    const data = (raw as any).data || raw;
    return { data };
  }

  static async getIncomeVsExpense(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<ApiResponse<IncomeVsExpense>> {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);
    const url = searchParams.toString()
      ? `${ReportsService.BASE_URL}/income-vs-expense?${searchParams.toString()}`
      : `${ReportsService.BASE_URL}/income-vs-expense`;
    const raw = await httpClient.get<any>(url);
    const data = (raw as any).data || raw;
    return { data };
  }

  static async getCategoryTrends(params?: {
    start_date?: string;
    end_date?: string;
    category_id?: string;
  }): Promise<ApiResponse<{ trends: CategoryTrend[] }>> {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);
    if (params?.category_id) searchParams.set('category_id', params.category_id);
    const url = searchParams.toString()
      ? `${ReportsService.BASE_URL}/category-trends?${searchParams.toString()}`
      : `${ReportsService.BASE_URL}/category-trends`;
    const raw = await httpClient.get<any>(url);
    const data = (raw as any).data || raw;
    return { data };
  }
}
